import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CustomerHeader from "@/components/CustomerHeader";
import Disclaimer from "@/components/Disclaimer";
import { FORTUNE_COVERS } from "@/lib/images";
import * as Icons from "lucide-react";
import { Sparkles, Coins, Gift, Play } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [types, setTypes] = useState([]);
  const [adBusy, setAdBusy] = useState(false);
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/fortune-types").then(({ data }) => setTypes(data));
  }, []);

  useEffect(() => {
    if (user?.role === "falci") nav("/falci-panel", { replace: true });
  }, [user, nav]);

  const watchAdReward = async () => {
    // In web MVP: simulate a 5-second "ad" via modal. Real AdMob only fires in mobile app.
    if (!window.confirm("(Web sürümü) 5 saniyelik reklam simüle edilecek → +5 jeton kazanacaksın. Devam?")) return;
    setAdBusy(true);
    await new Promise((r) => setTimeout(r, 3000));
    try {
      const { data } = await api.post("/rewards/watch-ad");
      toast.success(`+${data.reward} jeton kazandın! Bugün kalan hakkın: ${data.remaining_today}`);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ödül alınamadı");
    } finally { setAdBusy(false); }
  };

  return (
    <div className="cosmic-bg min-h-screen">
      <div className="stars" />
      <CustomerHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="fade-up mb-10">
          <p className="text-white/60 text-sm">Hoş geldin,</p>
          <h1 className="font-serif-myst text-4xl md:text-5xl mt-1">
            <span className="gold-text italic">{user?.name}</span>
          </h1>
          {user?.zodiac_sign && (
            <p className="text-white/70 mt-2 text-sm">Burcun: <span className="gold-text">{user.zodiac_sign}</span></p>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Link to="/magaza" data-testid="quick-tokens" className="glass rounded-2xl p-5 hover:border-[#D4AF37]/40 border border-white/5 transition-colors flex items-center justify-between">
            <div>
              <div className="text-xs text-white/60">Jetonların</div>
              <div className="font-serif-myst text-3xl gold-text mt-1">{user?.tokens ?? 0}</div>
            </div>
            <Coins className="w-8 h-8 gold-text opacity-70" />
          </Link>
          <button onClick={watchAdReward} disabled={adBusy} data-testid="watch-ad-btn" className="glass rounded-2xl p-5 hover:border-[#D4AF37]/40 border border-white/5 transition-colors flex items-center justify-between text-left">
            <div>
              <div className="text-xs text-white/60">Reklam izle, kazan</div>
              <div className="font-serif-myst text-2xl mt-1">+5 jeton</div>
            </div>
            <Play className="w-8 h-8 gold-text opacity-70" />
          </button>
          <Link to="/magaza" data-testid="quick-premium" className="glass rounded-2xl p-5 hover:border-[#D4AF37]/40 border border-white/5 transition-colors flex items-center justify-between">
            <div>
              <div className="text-xs text-white/60">Üyelik</div>
              <div className="font-serif-myst text-2xl mt-1">{user?.is_premium ? "Premium" : "Ücretsiz"}</div>
            </div>
            <Sparkles className="w-8 h-8 gold-text opacity-70" />
          </Link>
          <Link to="/profil" data-testid="quick-referral" className="glass rounded-2xl p-5 hover:border-[#D4AF37]/40 border border-white/5 transition-colors flex items-center justify-between">
            <div>
              <div className="text-xs text-white/60">Davet et +25</div>
              <div className="font-serif-myst text-xl gold-text mt-1 tracking-wider">{user?.referral_code}</div>
            </div>
            <Gift className="w-8 h-8 gold-text opacity-70" />
          </Link>
        </div>

        <h2 className="font-serif-myst text-2xl md:text-3xl mb-5">Fal türünü seç</h2>

        <Link to="/falcilar" data-testid="link-falcilar" className="block mb-6 relative overflow-hidden rounded-2xl border gold-border p-5 bg-gradient-to-r from-[#D4AF37]/15 to-transparent hover:from-[#D4AF37]/25 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest gold-text mb-1">Yeni ✨</div>
              <div className="font-serif-myst text-2xl">Gerçek Falcılara Yorumlat</div>
              <div className="text-sm text-white/60 mt-1">AI değil, deneyimli usta. Onaylı falcılar seni bekliyor.</div>
            </div>
            <div className="hidden sm:block gold-text text-4xl">→</div>
          </div>
        </Link>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4" data-testid="fortune-types-grid">
          {types.map((t, idx) => {
            const Icon = Icons[t.icon] || Sparkles;
            const size = idx === 0 ? "md:col-span-2 md:row-span-2" : "";
            const cover = FORTUNE_COVERS[t.slug] || t.cover_image;
            return (
              <Link
                key={t.id}
                to={`/fal/${t.slug}`}
                data-testid={`fortune-card-${t.slug}`}
                className={`rounded-3xl overflow-hidden hover:border-[#D4AF37]/40 border border-white/5 transition-colors group ${size}`}
              >
                <div
                  className={`w-full ${idx === 0 ? "aspect-[16/10]" : "aspect-[4/3]"} bg-cover bg-center relative`}
                  style={{ backgroundImage: cover ? `url(${cover})` : undefined }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute top-3 right-3 token-pill px-2.5 py-1 rounded-full text-xs flex items-center gap-1">
                    <Coins className="w-3 h-3" /> {t.token_cost}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <Icon className="w-5 h-5 gold-text mb-2" />
                    <div className={`font-serif-myst ${idx === 0 ? "text-3xl md:text-4xl" : "text-xl"} font-semibold`}>{t.name_tr}</div>
                    <div className="text-xs text-white/60 mt-1 line-clamp-2">{t.description}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <Disclaimer />
      </main>
    </div>
  );
}
